<template>
  <div v-if="isForbidden">
    <div
      class="mx-auto p-4 text-red-700 bg-red-200 border-red-700 border-solid border-2 rounded"
    >
      You are not allowed to view this page, if you think this is a mistake
      please contact us
    </div>
  </div>
  <DetailView
    v-else
    :title="sidney?.title ?? 'You need to be logged in to view this page'"
    :outline="outline"
    icon-for-type="Sidney"
    :actions="actions"
    sticky-sidebar
    actions-before
    :print-loading="printLoading"
    :expiry-date="expiryDate"
    @changeDate="(value) => (expiryDate = value)"
  >
    <template #heroSubtitle>
      <span v-if="sidney?.title">
        {{ sidney.subtitle }}
      </span>
      <a v-else :href="frontHost + '/?next=' + url.href"
        ><BasicButton>Login</BasicButton></a
      >
    </template>
    <template #heroDescription>
      <span
        v-for="(composer, index) in sidney.work?.composers"
        :key="`${composer.firstName} ${composer.lastName}`"
      >
        {{ composer.firstName }} {{ composer.lastName
        }}<span
          v-if="
            sidney.work?.composers.length > 1 &&
            index !== sidney.work?.composers.length - 1
          "
          >,
        </span>
      </span>
    </template>
    <FeedbackNotification
      v-if="!route.query?.print"
      class="mt-6 border-primary-400 md:mt-0 print:hidden"
    >
      <template #title>
        The setup and the execution of the electroacoustic part of this work
        requires a Computer Music Designer (Max expert).
      </template>
    </FeedbackNotification>
    <div
      id="version-information"
      class="border-black border-2 p-4 mt-6"
      v-if="Object.keys(sidney).length !== 0"
    >
      <h3
        class="text-mobile-lg font-semibold first-letter:uppercase md:text-base"
      >
        Version Information
      </h3>
      <DataDescriptionList
        class="mt-4"
        style="font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif"
        :items="versionInformation"
      />
    </div>
    <div
      id="other-versions"
      class="border-black border-2 p-4 mt-6"
      v-if="Object.keys(sidney).length !== 0"
    >
      <h3
        v-if="sidney.versions?.length > 0"
        class="text-mobile-lg font-semibold first-letter:uppercase md:text-base"
      >
        Other Versions
      </h3>
      <h3
        v-else
        class="text-mobile-lg font-semibold first-letter:uppercase md:text-base"
      >
        No other versions
      </h3>
      <div class="mt-2 flex flex-col gap-4">
        <div v-for="(version, i) in sidney.versions" :key="i">
          <h4 class="text-mobile-lg md:text-base flex gap-2 items-center">
            <BasicLink
              :to="`/sidney/${version.slug}`"
              class="underline text-primary-600 hover:text-primary-600 hover:font-bold"
              >{{ version.name }}</BasicLink
            >
            <DataPill is-gray :highlight="version.status === 'valid'">{{
              version.status
            }}</DataPill>
            <span class="ml-4 text-shade-500">{{
              formatToFullDate(version.version_date?.date)
            }}</span>
          </h4>
        </div>
      </div>
    </div>
    <div
      id="detailed-staff"
      class="border-black border-2 p-4 mt-6"
      v-if="Object.keys(sidney).length !== 0"
    >
      <h3
        class="text-mobile-lg font-semibold first-letter:uppercase md:text-base"
      >
        Detailed Staff
      </h3>
      <DataDescriptionList class="mt-4" :items="detailedstaff.slice(0, 1)" />
      <p
        class="text-mobile-lg md:text-lg basis-36 sm:py-2 sm:pr-2 lg:basis-48 xl:basis-60"
      >
        {{ detailedstaff[1].value }}
      </p>
      <p class="mt-2 text-shade-500">
        Detailed staff comes from Brahms, send mail to
        <a
          href="mailto:ressources-contenus@ircam.fr"
          class="text-primary-400 duration-200 ease-in-out hover:text-primary-100"
          >ressources-contenus@ircam.fr</a
        >
        for correction.
      </p>
    </div>
    <div
      v-if="channelDetails.find(({ value }) => value)"
      id="channel-details"
      class="border-black border-2 p-4 mt-6"
    >
      <h3
        class="text-mobile-lg font-semibold first-letter:uppercase md:text-base"
      >
        Channel Details
      </h3>
      <DataDescriptionList class="mt-4" :items="channelDetails" />
    </div>
    <div
      id="electronic-equipment-list"
      class="border-black border-2 p-4 mt-6"
      v-if="electronicEquipmentList?.length > 0"
    >
      <h3
        class="text-mobile-lg font-semibold first-letter:uppercase md:text-base"
      >
        Electronic Equipment List
      </h3>
      <div v-for="equipment in electronicEquipmentList">
        <h3
          class="mt-2 text-mobile-lg font-bold first-letter:uppercase md:text-base"
        >
          {{ equipment[0] }}
        </h3>
        <DataDescriptionList
          class="mt-4"
          style="font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif"
          :items="equipment[1]"
        />
      </div>
    </div>
    <div
      id="work-related-information"
      class="border-black border-2 p-4 mt-6"
      v-if="Object.keys(sidney).length !== 0"
    >
      <h3
        class="text-mobile-lg font-semibold first-letter:uppercase md:text-base"
      >
        Work Related Information
      </h3>
      <DataDescriptionList class="mt-4" :items="workRelatedInformation" />
    </div>
    <h3
      v-if="!route.query?.print && Object.keys(sidney).length !== 0"
      class="mt-6 text-mobile-lg font-semibold first-letter:uppercase md:mt-10 md:text-base print:hidden"
      id="downloads"
    >
      Downloads
    </h3>
    <div
      v-if="!route.query?.print"
      class="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2 print:hidden"
    >
      <CardFile
        v-for="file in sidney.setup?.files"
        :key="file.path"
        :file="file"
      />
    </div>
    <hr class="mt-6 border-shade-200 md:mt-10" />
    <h3
      v-if="sidney.setup?.instructions"
      class="mt-6 px-12 text-mobile-lg font-semibold first-letter:uppercase md:mt-10 md:text-base"
      id="instructions"
    >
      Instructions
    </h3>
    <DataProse
      class="mt-6 md:mt-10 break-all md:break-normal"
      style="font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif"
    >
      <template #default="{ classes }">
        <div
          v-if="route.query?.print"
          :class="classes"
          v-html="formatInstructionsPrint(sidney.setup?.instructions)"
        />
        <div v-else :class="classes" v-html="sidney.setup?.instructions" />
      </template>
    </DataProse>
    <template #contentMisc>
      <h3
        v-if="sidney.program"
        id="channeldetails"
        class="mt-6 px-12 text-mobile-lg font-semibold first-letter:uppercase md:mt-10 md:text-base"
      >
        Program Notes
      </h3>
      <DataProse
        v-if="sidney.program"
        class="mt-6 px-2 md:mt-10"
        style="font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif"
      >
        <template #default="{ classes }">
          <span
            v-if="route.query?.print"
            :class="classes"
            v-html="formatInstructionsPrint(sidney.setup?.instructions)"
          />
          <span
            v-else
            :class="classes"
            v-html="formatInstructionsPrint(sidney.program)"
          />
        </template>
      </DataProse>
    </template>
    <template #sidebarDetails>
      <span
        v-if="sidney.update_date"
        class="inline-block first-letter:uppercase"
      >
        Updated at
      </span>
      <span
        v-else-if="sidney.create_date"
        class="inline-block first-letter:uppercase"
      >
        Created at
      </span>
    </template>
  </DetailView>
</template>
<script lang="ts" setup>
import { useI18n } from '#i18n'
import Sidney from '../../../components/navigation/Sidney'
import { fmtMSS } from '~/const/conversion'
import { getSidneyVersionBySlug } from '~/queries/sidney'

const { locales, t } = useI18n()

const runtimeConfig = useRuntimeConfig()
const isForbidden = ref(false)
const domain = runtimeConfig.public.DOMAIN
const frontHost = runtimeConfig.public.FRONT_HOST
const url = useRequestURL()
const token = useCookie('ressources_user_token', {
  domain,
})

const { formatToFullDate } = useFullDateFormater()

const isLoggedIn = async () => {
  if (!token.value) return false
  const result = await authFetch('isSidneyUser')
  return result
}

function formatInstructionsPrint(instructions) {
  if (!instructions) {
    return ''
  }
  return instructions
    .replaceAll('<em>', '')
    .replaceAll('<em style="line-height: 1.5em;">', '')
    .replaceAll('</em>', '')
    .replaceAll('<span style="line-height: 1.5em;">', '<span>')
}

const route = useRoute()

const loggedIn = await isLoggedIn()

const getLocaleISO = (code) => {
  const locale = locales.value.find((i) => i.code === code)
  return locale ? locale.iso : code
}

const { slug } = useResourceSlug()
const { data, pending, error } = await useAsyncData(
  `/sidney/${slug}/`,
  async (context) => {
    // try {
    const result = await execQuery(getSidneyVersionBySlug, { slug })
    return result
    // } catch (error) {
    //   if (error.message === 'Redirect') {
    //     const url = useRequestURL()
    //
    //     return externalRedirect(`${frontHost}/?next=${url.href}`)
    //   }
    //   if (error.message === 'Forbidden') {
    //     isForbidden.value = true
    //     return
    //   }
    //   throw error
    // }
  }
)
const sidney = computed<SidneyVersion>(() => {
  if (pending.value || error.value) return {}
  return data.value.data.versionBySlug
})

let canValidate
if (sidney.value?.id) {
  canValidate = await authFetch(`canValidate?id=${sidney.value.id}`)
}

const updateDate = computed<string>(() => {
  if (!sidney.value.update_date) return null
  return formatToFullDate(sidney.value.update_date.date)
})
const createDate = computed<string>(() => {
  if (!sidney.value.create_date) return null
  return formatToFullDate(sidney.value.create_date.date)
})

const composers = computed(() => {
  if (
    !sidney.value.work ||
    !sidney.value.work?.composers ||
    sidney.value.work?.composers?.length === 0
  )
    return ''
  return sidney.value.work.composers
    .map(({ firstName, lastName }) => `${firstName} ${lastName}`)
    .join(', ')
})

const versionInformation = computed<DescriptionList>(() => {
  return [
    {
      key: 'Performance date',
      value: formatToFullDate(sidney.value.performedAt),
    },
    {
      key: 'Documentation date',
      value: formatToFullDate(sidney.value.version_date?.date),
    },
    {
      key: 'Version',
      value: sidney.value.subtitle,
    },
    {
      key: 'Status',
      value: sidney.value.status,
    },
    ...(sidney.value.validation_date
      ? [
          {
            key: 'Validation date',
            value: formatToFullDate(sidney.value.validation_date?.date),
          },
        ]
      : []),
    ...(sidney.value.documentalists?.filter((doc) => doc)?.length > 0
      ? [
          {
            key: 'Documentalist',
            value: sidney.value.documentalists
              ?.filter((doc) => doc)
              .map(({ firstName, lastName, email, username }) => {
                let name
                if (username && (!firstName || !lastName)) {
                  name = username
                } else {
                  name = `${firstName ?? ''} ${lastName ?? ''}`
                }
                if (email) {
                  name += ` (${email})`
                }
                return name
              }),
          },
        ]
      : []),
    ...(sidney.value.realisators?.length > 0
      ? [
          {
            key: 'Realisation',
            value: sidney.value.realisators?.map((realisator) => {
              let name = `${realisator.person.firstName} ${realisator.person.lastName}`
              if (realisator.role) {
                name += ` (${realisator.role.name})`
              }
              return name
            }),
          },
        ]
      : []),
    {
      key: 'Length',
      value: fmtMSS(sidney.value?.length),
    },
    {
      key: 'Upgrade motivation',
      value: sidney.value.upgrade_motivation,
    },
    {
      key: 'Comment',
      value: sidney.value.comment,
    },
  ]
})

const channelDetails = computed<DescriptionList>(() => {
  return [
    {
      key: 'Number of input channels',
      value: sidney.value.setup?.input_channels,
    },
    {
      key: 'Number of output channels',
      value: sidney.value.setup?.output_channels,
    },
  ]
})

const detailedstaff = computed<DescriptionList>(() => {
  return [
    {
      key: 'Soloist',
      value: sidney.value.setup?.detailed_staff?.solo,
    },
    {
      key: 'Others',
      value: sidney.value.setup?.detailed_staff?.others,
    },
  ]
})

const electronicEquipmentList = computed<DescriptionList>(() => {
  if (!sidney.value.setup?.electronic_equipments) return null
  return groupBy(
    sidney.value.setup?.electronic_equipments,
    ({ category }) => category,
    { asList: true }
  ).map(([category, electronicEquipments]) => {
    return [
      category,
      electronicEquipments.map((electronicEquipment) => {
        const equipment = electronicEquipment?.equipments?.[0]
        return {
          key: `${equipment?.number} ${electronicEquipment?.title}`,
          value: equipment?.equipment?.title,
        }
      }),
    ]
  })
})

const workRelatedInformation = computed<DescriptionList>(() => {
  const result = []

  if (sidney.value.work?.creations?.length > 0) {
    result.push({
      key: 'Premiere',
      value: sidney.value.work?.creations.map(
        (creation) =>
          `<span>${formatToFullDate(
            creation.date,
            creation.dateAccuracy
          )},&nbsp; ${creation.location}</span>`
      ),
    })
  }
  if (sidney.value.work?.editor) {
    result.push({
      key: 'Publisher',
      value: sidney.value.work?.editor,
    })
  }
  if (sidney.value.realisator) {
    result.push({
      key: 'Computer Music Design',
      value: sidney.value.realisator,
    })
  }
  if (sidney.value.length) {
    result.push({
      key: 'Work length',
      value: fmtMSS(sidney.value.length),
    })
  } else {
    result.push({
      key: t('piece.duration'),
      value: undefined,
    })
  }

  return result
})

const outline = computed<SidebarMenu>(() => {
  if (sidney.value.toc) {
    sidney.value.toc = [
      {
        title: 'Version Information',
        anchor: 'version-information',
      },
      ...(sidney.value.versions?.length > 0
        ? [
            {
              title: 'Other Versions',
              anchor: 'other-versions',
            },
          ]
        : []),
      {
        title: 'Detailed Staff',
        anchor: 'detailed-staff',
      },
      ...(channelDetails.value.find(({ value }) => value)
        ? [
            {
              title: 'Channel Details',
              anchor: 'channel-details',
            },
          ]
        : []),
      {
        title: 'Electronic Equipment List',
        anchor: 'electronic-equipment-list',
      },
      {
        title: 'Work Related Information',
        anchor: 'work-related-information',
      },
      {
        title: 'Downloads',
        anchor: 'downloads',
      },
      {
        title: 'Instructions',
        anchor: 'instructions',
        children: sidney.value.toc,
      },
    ]
  }
  return {
    title: 'Outline',
    items: sidney.value.toc,
  }
})

const forkVersion = () => {
  window.open(
    `${frontHost}/ressources/work_versions?id=${sidney.value.id}&new=true`
  )
}
const printLoading = ref(false)
const printVersion = () => {
  printLoading.value = true
  print(`${window.location.href}`)
    .then(() => (printLoading.value = false))
    .catch(() => (printLoading.value = false))
}
const expiryDate = ref(
  new Date(Date.now() + 12096e5).toISOString().split('T')[0]
)
const mailVersion = () => {
  window.open(
    `${frontHost}/ressources/sidney_sharings?key=versionId&value=${sidney.value.id}&expiry=${expiryDate.value}`
  )
}
const editVersion = () => {
  window.open(`${frontHost}/ressources/work_versions?id=${sidney.value.id}`)
}
const validateVersion = () => {
  window.open(
    `${frontHost}/ressources/sidney_validations?key=versionId&value=${sidney.value.id}`
  )
}
const actions = ref<Actions>([
  ...(Object.keys(sidney.value).length !== 0
    ? [
        {
          label: 'Print version',
          action: printVersion,
        },
      ]
    : []),
  ...(loggedIn
    ? [
        {
          label: 'Mail version',
          action: mailVersion,
        },
      ]
    : []),
  ...(loggedIn
    ? [
        {
          label: 'Fork version',
          action: forkVersion,
        },

        {
          label: 'Edit version',
          action: editVersion,
        },
      ]
    : []),
  ...(loggedIn && canValidate
    ? [
        {
          label: 'Validate',
          action: validateVersion,
        },
      ]
    : []),
])
</script>
